import { type ReactNode } from "react";
import {
  type ClientLoaderFunctionArgs,
  Links,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "@remix-run/react";
import * as SentryReact from "@sentry/react";
import i18next from "./i18n/i18next.server";
import { PATHS_HYDRATED, PATHS_WITHOUT_HEADER } from "./entry.server";
import { ErrorPage } from "./components/shared/ErrorPage";

interface ClientENV {
  APP_URL: string;
  ENVIRONMENT: string;
  NODE_ENV: string;
  HASH: string;
  RENDER_MODE: string;
  WITHOUT_HEADER: boolean;
  APP_PROXY_CONFIG?: unknown;
}

declare global {
  interface Window {
    ENV: ClientENV;
  }
}

export const loader = async ({ request }: ClientLoaderFunctionArgs) => {
  const locale = await i18next.getLocale(request);
  const { url } = request;
  const searchParams = new URL(url).searchParams;
  const isHydrated = PATHS_HYDRATED.some((path) => url.includes(path));
  const isEmbedded = searchParams.get("embedded") === "1";
  const isWithoutHeader = PATHS_WITHOUT_HEADER.some((path) =>
    url.includes(path),
  );

  // Import app proxy config for server-side processing
  const { APP_PROXY_CONFIG } = await import("./config/app-proxy.js");

  return {
    locale,
    ENV: {
      APP_URL: process.env.SHOPIFY_APP_URL || process.env.APP_URL,
      ENVIRONMENT: process.env.DD_ENV || "unknown",
      NODE_ENV: process.env.NODE_ENV ?? "development",
      HASH: process.env.GITHUB_SHA,
      RENDER_MODE: isEmbedded || isHydrated ? "hydrate" : "create",
      WITHOUT_HEADER: isWithoutHeader,
      // App proxy configuration for client-side use
      APP_PROXY_CONFIG,
    },
  };
};

function ClientErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <SentryReact.ErrorBoundary
      fallback={({ error }) => <ErrorPage message={error?.toString()} />}
    >
      {children}
    </SentryReact.ErrorBoundary>
  );
}

export default function App() {
  const { ENV }: { ENV: ClientENV } = useLoaderData();

  return (
    <>
      <Links />
      {ENV.RENDER_MODE === "create" && ENV.WITHOUT_HEADER === true && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              /* Hide all elements except #MainContent if header is not present */
              /* #smct-tag-debugger is excluded so the Intently tag debugger stays visible */
              body > *:not(#MainContent):not([role="dialog"]):not([data-slot="dialog-overlay"]):not(.modal-backdrop):not([data-radix-popper-content-wrapper]):not(#smct-tag-debugger),
              body > *:not(#MainContent):not([role="dialog"]):not([data-slot="dialog-overlay"]):not(.modal-backdrop):not([data-radix-popper-content-wrapper]):not(#smct-tag-debugger) * {
                display: none !important;
              }
            `,
          }}
        />
      )}
      <div className="tw:w-full">
        <ClientErrorBoundary>
          <Outlet />
        </ClientErrorBoundary>
      </div>
      <ScrollRestoration />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}; window.__RENDER_MODE__ = "${ENV.RENDER_MODE}";`,
        }}
      />
      <Scripts />
    </>
  );
}
